import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import HTMLFlipBook from 'react-pageflip'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Box from '@material-ui/core/Box'
import { formatAmount } from '../../utility'
import CardPreview from '../Cards/CardPreview'
import { Image, Transformation } from 'cloudinary-react'
import { CLOUDINARY_NAME } from '../../constants'
import useTheme from '@material-ui/core/styles/useTheme'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Skeleton from '@material-ui/lab/Skeleton'

const useStyles = makeStyles((theme) => ({
    greetingCardContainer: {
        marginBottom: '2rem',
        transform: 'translateX(-52%) !important ',
        transition: 'transform 0.5s',
    },
    greetingCardOpen: {
        transform: 'translateX(0) !important',
        transition: 'transform 0.5s',
    },
    greetingCardPage: {
        boxShadow: `0 0.3px 0.9px rgba(0, 0, 0, 0.037), 
            0 0.8px 2.2px rgba(0, 0, 0, 0.053), 
            0 1.5px 4.1px rgba(0, 0, 0, 0.065), 
            0 2.7px 7.4px rgba(0, 0, 0, 0.077), 
            0 5px 13.8px rgba(0, 0, 0, 0.093), 
            0 12px 33px rgba(0, 0, 0, 0.13)`,
    },
    greetingCardPageContainer: (props) => {
        return {
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'white',
            backgroundImage: `url(${props.backgroundImageUrl})`,
            padding: '5%',
        }
    },
    noSidePadding: {
        padding: '5% 0 5% 0 !important',
    },
    singlePageCarrierInfoPageContainer: {
        [theme.breakpoints.down(360)]: {
            height: '75%',
        },
        backgroundImage: `url("https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_284/v1635259997/GCG/assets/greeting-card-panel-left_d1zvcn")`,
        width: '55%',
        backgroundSize: 'cover',
        height: '82%',
        padding: '5% 0 5% 0',
        margin: '0 auto',
        fontSize: '1rem',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    carrierInfoPageContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        overflowWrap: 'break-word',
        overflowY: 'hidden',
    },
    handwrytten: {
        fontFamily: 'Handwrytten',

        fontStyle: 'normal',
        lineHeight: '.7em !important',
        color: '#000',
    },
    carrierInfoPageLabel: {
        fontWeight: 'bold',
        fontSize: '9px',
        [theme.breakpoints.up('sm')]: {
            fontSize: '10px',
        },
    },
    carrierInfoPageText: {
        margin: '0',
        padding: '0',
    },
    carrierInfoPageMessage: {
        fontStyle: 'italic',
        whiteSpace: 'pre-line',
        fontSize: 'inherit',
        [theme.breakpoints.up('sm')]: {
            whiteSpace: 'pre-line',
        },
    },
    greetingCardImageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexDirection: 'column',
        height: '100%',
        gap: '3px',
    },
    greetingCardImage: {
        height: '100%',
        width: '100%',
        opacity: 1,
        transition: 'opacity 300ms',
    },
    cardImage: {
        borderRadius: 6,
        width: '100%',
    },
    relativeContainer: {
        position: 'relative',
    },
    cardImageOnModal: {
        borderRadius: 6,
        width: '100%',
    },
    cardCtrlBtn: {
        backgroundColor: '#eee',
        textTransform: 'none',
        border: 'none',
        margin: '0 11px',
        borderRadius: '8px !important',
        fontWeight: 'bold',
        lineHeight: '1.1em',
        padding: '14px 10px',
        color: '#20202080',
        width: '92px',
        [theme.breakpoints.up('sm')]: {
            margin: '0 30px',
        },
        '&:hover': {
            border: 'none',
        },
    },
    activePageButton: {
        backgroundColor: '#005C59 !important',
        color: 'white',
    },
    fileIcon: {
        position: 'absolute',
        right: '17px',
    },
    bookIcon: {
        position: 'absolute',
        right: '11px',
    },
    text: {
        marginRight: '23px',
        fontSize: '13px',
    },
    cardAmount: {
        fontSize: '10px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '8px',
        },
    },
}))

const SinglePageCarrierContainer = React.forwardRef((props, ref) => {
    const classes = useStyles(props)

    const containerRef = useRef(null)
    const [elementWidth, setElementWidth] = useState(0)

    useEffect(() => {
        let resizeObserverTimeout
        const element = containerRef?.current
        const observer = new ResizeObserver((entries) => {
            resizeObserverTimeout = setTimeout(() =>
                setElementWidth(entries[0].contentRect.width)
            )
        })
        observer.observe(element)

        return () => {
            observer.disconnect()
            clearTimeout(resizeObserverTimeout)
        }
    }, [])

    return (
        <div
            ref={containerRef}
            style={{
                fontSize: props.handWrytten
                    ? elementWidth / 7.5
                    : elementWidth / 11.5,
                paddingLeft: elementWidth / 10,
                paddingRight: elementWidth / 10,
                color: props.handWrytten ? '#173fa6' : '#222',
            }}
            className={classes.carrierInfoPageContainer}
        >
            <div
                className={`${classes.carrierInfoPageText} ${
                    classes.carrierInfoPageMessage
                } ${props.handWrytten ? classes.handwrytten : ''}`}
                data-testid="card-message"
            >
                {props.carrierMessage}
            </div>
        </div>
    )
})

const PageCover = React.forwardRef((props, ref) => {
    const classes = useStyles(props)
    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'), {
        defaultMatches: true,
    })
    // Show Skeleton if loadingCover is true or coverImageCloudinaryId is empty
    if (props.loadingCover || !props.coverImageCloudinaryId) {
        return (
            <div ref={ref} className={classes.greetingCardPage}>
                <Skeleton
                    animation="wave"
                    width={213}
                    height={283}
                    variant="rect"
                />
            </div>
        )
    }
    return (
        <div
            ref={ref}
            data-density="hard"
            className={`${classes.greetingCardPage}`}
        >
            <Image
                cloudName={CLOUDINARY_NAME}
                publicId={props.coverImageCloudinaryId}
                loading="lazy"
                className={classes.greetingCardImage}
                title={`Greeting card cover design`}
                alt={`Greeting card cover design`}
                height={isSmall ? 561 : 415}
                data-testid="selected-greeting-card"
            >
                {isSmall ? (
                    <Transformation rawTransformation="h_561" />
                ) : (
                    <Transformation rawTransformation="h_415" />
                )}
                <Transformation quality="auto" fetchFormat="auto" />
            </Image>
        </div>
    )
})

const Page = React.forwardRef((props, ref) => {
    const classes = useStyles(props)

    return (
        <div
            ref={ref}
            className={`${classes.greetingCardPage} ${classes.greetingCardPageContainer} ${props.className}`}
            style={null}
        >
            {props.children}
        </div>
    )
})

const DynamicGreetingCard = ({
    amount,
    cardNetworkName,
    displayPageButtons,
    carrierFrom,
    carrierMessage,
    carrierType,
    carrierTo,
    cloudinaryId,
    greetingCard,
    messageOnCard,
    nameOnCard,
    isOnModal,
    wantToSelectGreetingCard,
    openBook,
    handWrytten = false,
    className,
}) => {
    const greetingCardRef = useRef()
    const [activePage, setActivePage] = useState(0)
    const [toggleBook, setToggleBook] = useState(1)
    const props = { wantToSelectGreetingCard, handWrytten }
    const classes = useStyles(props)
    const openBookButtonClick = () => {
        setActivePage(1)
        if (!toggleBook) {
            greetingCardRef.current?.pageFlip()?.turnToPage(1)
            setToggleBook(1)
        } else if (toggleBook && activePage === 0) {
            greetingCardRef.current?.pageFlip()?.flipNext()
        }
    }

    const closeBookButtonClick = () => {
        setActivePage(0)
        if (!toggleBook) {
            greetingCardRef.current?.pageFlip()?.turnToPage(0)
            setToggleBook(1)
        } else if (toggleBook && activePage === 1) {
            greetingCardRef.current.pageFlip().flipPrev()
        }
    }

    return (
        <>
            <div className={classes.relativeContainer}>
                <HTMLFlipBook
                    ref={greetingCardRef}
                    width={550}
                    height={733}
                    size="stretch"
                    drawShadow={false}
                    showCover={true}
                    autoSize={true}
                    mobileScrollSupport={true}
                    useMouseEvents={false}
                    usePortrait={false}
                    style={{ visibility: toggleBook ? 'visible' : 'hidden' }}
                    onInit={openBook ? openBookButtonClick : ''}
                    className={`${classes.greetingCardContainer} ${
                        activePage === 1
                            ? classes.greetingCardOpen
                            : `${className}`
                    }`}
                >
                    <PageCover
                        key="cover"
                        coverImageCloudinaryId={greetingCard?.cloudinary_id}
                        loadingCover={!greetingCard?.cloudinary_id}
                    />

                    <Page
                        key="left"
                        className={classes.noSidePadding}
                        backgroundImageUrl={
                            'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_284/v1635259997/GCG/assets/greeting-card-panel-left_d1zvcn'
                        }
                    >
                        <SinglePageCarrierContainer
                            carrierTo={carrierTo}
                            carrierFrom={carrierFrom}
                            carrierMessage={carrierMessage}
                            amount={amount}
                            carrierType={carrierType}
                            handWrytten={handWrytten}
                        />
                    </Page>
                    <Page
                        key="right"
                        backgroundImageUrl={
                            'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,w_284/v1635259998/GCG/assets/greeting-card-panel-right_gsjbov'
                        }
                    >
                        {cloudinaryId && (
                            <div className={classes.greetingCardImageContainer}>
                                <CardPreview
                                    cloudinaryId={cloudinaryId}
                                    emboss1={nameOnCard}
                                    emboss2={messageOnCard}
                                    cardNetworkName={cardNetworkName}
                                    hideOverlay={false}
                                    showDynamicEmboss={true}
                                    imageClassName={
                                        isOnModal
                                            ? classes.cardImageOnModal
                                            : classes.cardImage
                                    }
                                />
                                <div>
                                    <p
                                        className={classes.carrierInfoPageText}
                                        data-testid="card-value"
                                    >
                                        <span
                                            className={
                                                classes.carrierInfoPageLabel
                                            }
                                        >
                                            Value:{' '}
                                        </span>{' '}
                                        <span className={classes.cardAmount}>
                                            ${formatAmount(amount)}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        )}
                    </Page>
                </HTMLFlipBook>

                <div
                    className={classes.singlePageCarrierInfoPageContainer}
                    style={{ visibility: toggleBook ? 'hidden' : 'visible' }}
                >
                    <SinglePageCarrierContainer
                        carrierTo={carrierTo}
                        carrierFrom={carrierFrom}
                        carrierMessage={carrierMessage}
                        carrierType={carrierType}
                        amount={amount}
                        handWrytten={handWrytten}
                    />
                </div>
                {displayPageButtons && (
                    <Box display="flex" justifyContent="center">
                        <ButtonGroup
                            color="primary"
                            variant="outlined"
                            size="large"
                            style={{ marginBottom: '2rem' }}
                        >
                            <Button
                                className={`${classes.cardCtrlBtn} ${
                                    activePage === 0
                                        ? classes.activePageButton
                                        : ''
                                }`}
                                onClick={closeBookButtonClick}
                                size="medium"
                                aria-label="View the front cover of your greeting card"
                            >
                                <span className={classes.text}>Front</span>
                                <svg
                                    className={classes.fileIcon}
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="24"
                                    viewBox="0 0 18 24"
                                    fill="none"
                                >
                                    <path
                                        d="M4.71098 19.8258C4.40268 19.8258 4.15043 19.5699 4.15043 19.257V5.60577C4.15043 5.29293 4.40268 5.03697 4.71098 5.03697H10.3166V7.88098C10.3166 8.51022 10.8176 9.01859 11.4377 9.01859H14.2405V19.257C14.2405 19.5699 13.9882 19.8258 13.6799 19.8258H4.71098ZM4.71098 3.33057C3.47425 3.33057 2.46875 4.35085 2.46875 5.60577V19.257C2.46875 20.5119 3.47425 21.5322 4.71098 21.5322H13.6799C14.9166 21.5322 15.9222 20.5119 15.9222 19.257V8.82306C15.9222 8.21871 15.6874 7.63924 15.267 7.21264L12.0928 3.99535C11.6724 3.56875 11.1049 3.33057 10.5093 3.33057H4.71098ZM8.07433 12.4314C8.07433 12.1297 7.95622 11.8403 7.74597 11.627C7.53572 11.4136 7.25056 11.2938 6.95322 11.2938C6.65588 11.2938 6.37072 11.4136 6.16047 11.627C5.95022 11.8403 5.8321 12.1297 5.8321 12.4314C5.8321 12.7331 5.95022 13.0225 6.16047 13.2358C6.37072 13.4491 6.65588 13.569 6.95322 13.569C7.25056 13.569 7.53572 13.4491 7.74597 13.2358C7.95622 13.0225 8.07433 12.7331 8.07433 12.4314ZM10.4987 14.0987C10.3936 13.9458 10.222 13.8534 10.0363 13.8534C9.8506 13.8534 9.67893 13.9458 9.57383 14.0987L8.12689 16.221L7.70997 15.542C7.60837 15.3749 7.42619 15.2754 7.2335 15.2754C7.0408 15.2754 6.86213 15.3749 6.75702 15.542L5.35563 17.8172C5.24702 17.9914 5.24351 18.2118 5.34161 18.3932C5.43971 18.5745 5.6289 18.6847 5.8321 18.6847H12.5588C12.769 18.6847 12.9582 18.5673 13.0563 18.3789C13.1544 18.1905 13.1404 17.9665 13.0213 17.7924L10.4987 14.0951V14.0987Z"
                                        fill="white"
                                    />
                                </svg>
                            </Button>

                            <Button
                                className={`${classes.cardCtrlBtn} ${
                                    activePage === 1
                                        ? classes.activePageButton
                                        : ''
                                }`}
                                onClick={openBookButtonClick}
                                size="medium"
                                aria-label="View the inside of your greeting card"
                            >
                                <span className={classes.text}>Inside</span>
                                <svg
                                    className={classes.bookIcon}
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="17"
                                    viewBox="0 0 20 17"
                                    fill="none"
                                    style={{ marginRight: '2px' }}
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.94913 0.940589C8.1532 0.474162 6.91744 0.0387573 5.42012 0.0387573C3.71932 0.0387573 2.12966 0.538376 1.17991 0.944081C0.799331 1.10937 0.570312 1.5301 0.570312 1.98464V15.8951C0.570312 16.3421 1.00141 16.6539 1.38198 16.5149C2.44288 16.1205 4.12347 15.6659 5.42012 15.6659C6.51747 15.6659 7.96328 16.1518 8.86943 16.5064C8.90607 16.5207 8.94183 16.5348 8.97665 16.5487C9.34039 16.6915 9.73107 16.3947 9.73107 15.9664V1.7893C9.73107 1.63153 9.67718 1.47375 9.56267 1.37608C9.40508 1.23618 9.19873 1.08686 8.94913 0.940589ZM7.73107 14.0206V2.55424C7.23229 2.30549 6.43227 2.03876 5.42012 2.03876C4.37572 2.03876 3.34931 2.28301 2.57031 2.55131V14.0632C3.4862 13.8372 4.49964 13.6659 5.42012 13.6659C6.17251 13.6659 6.9883 13.8207 7.73107 14.0206Z"
                                        fill="white"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M19.0341 0.940589C18.2382 0.474162 17.0024 0.0387573 15.5051 0.0387573C13.8043 0.0387573 12.2146 0.538376 11.2649 0.944081C10.8843 1.10937 10.6553 1.5301 10.6553 1.98464V15.8951C10.6553 16.3421 11.0864 16.6539 11.4669 16.5149C12.5278 16.1205 14.2084 15.6659 15.5051 15.6659C16.6024 15.6659 18.0482 16.1518 18.9544 16.5064C18.991 16.5207 19.0268 16.5348 19.0616 16.5487C19.4253 16.6915 19.816 16.3947 19.816 15.9664V1.7893C19.816 1.63153 19.7621 1.47375 19.6476 1.37608C19.49 1.23618 19.2837 1.08686 19.0341 0.940589ZM17.816 14.0206V2.55424C17.3172 2.30549 16.5172 2.03876 15.5051 2.03876C14.4607 2.03876 13.4343 2.28301 12.6553 2.55131V14.0632C13.5712 13.8372 14.5846 13.6659 15.5051 13.6659C16.2575 13.6659 17.0733 13.8207 17.816 14.0206Z"
                                        fill="white"
                                        style={{ marginRight: '2px' }}
                                    />
                                </svg>
                            </Button>
                        </ButtonGroup>
                    </Box>
                )}
            </div>
        </>
    )
}

export default DynamicGreetingCard

DynamicGreetingCard.propTypes = {
    greetingCard: PropTypes.object,
    displayPageButtons: PropTypes.bool.isRequired,
    amount: PropTypes.number,
    cardNetworkName: PropTypes.string,
    carrierFrom: PropTypes.string,
    carrierMessage: PropTypes.string,
    carrierType: PropTypes.string,
    carrierTo: PropTypes.string,
    cloudinaryId: PropTypes.string,
    messageOnCard: PropTypes.string,
    nameOnCard: PropTypes.string,
    className: PropTypes.any,
}
